import { createMultiStyleConfigHelpers, ThemingProps } from '@chakra-ui/react'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Radii, Spacing } from '~/types/ThemeTypes'
import {
  createCustomStyleContext,
  useCustomMultiStyleConfig,
} from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

const { Dropzone } = themeColorSectionKeys.shared

const dropzoneStyleParts = ['container', 'text', 'icon', 'clearButton'] as const
const helpers = createMultiStyleConfigHelpers(dropzoneStyleParts)

export enum DropzoneVariant {
  Default = 'default',
  Error = 'error',
  Active = 'active',
  FileAccepted = 'fileAccepted',
  DragAccepted = 'dragAccepted',
}

export const DropzoneStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      alignContent: 'center',
      justifyContent: 'center',
      borderRadius: Radii.md,
      backgroundColor: Dropzone.Background.Default,
      border: '1px dashed',
      borderColor: Dropzone.Border.Default,
      boxSizing: 'border-box',
      userSelect: 'none',
      outline: '2px solid transparent',
      outlineOffset: '2px',
      width: '100%',
      minHeight: '88px',
      textAlign: 'center',
      pl: Spacing._5,
      pr: Spacing._5,
      cursor: 'pointer',
      _hover: {
        borderColor: Dropzone.Border.Hover,
      },
      _active: {
        borderColor: Dropzone.Border.Active,
        borderWidth: '2px',
      },
      _focus: {
        borderColor: Dropzone.Border.Active,
        borderWidth: '2px',
      },
    },
    text: {
      textStyle: textStyleKeys.Body2,
      textAlign: 'center',
      color: Dropzone.FontColor.Default,
    },
    icon: {
      color: Dropzone.Icon,
      height: '2rem',
      width: '1.75rem',
    },
  },
  variants: {
    [DropzoneVariant.Error]: {
      container: {
        backgroundColor: Dropzone.Background.Error,
        borderColor: Dropzone.Border.Error,
        _hover: {
          borderColor: Dropzone.Border.Error,
        },
        _active: {
          borderColor: Dropzone.Border.Error,
        },
      },
      text: {
        color: Dropzone.FontColor.Error,
      },
    },
    [DropzoneVariant.Active]: {
      container: {
        borderColor: Dropzone.Border.Active,
        borderWidth: '2px',
        borderStyle: 'dashed',
        _hover: {
          borderColor: Dropzone.Border.Active,
        },
        _active: {
          borderColor: Dropzone.Border.Active,
        },
      },
    },
    [DropzoneVariant.DragAccepted]: {
      container: {
        backgroundColor: Dropzone.Background.DragAccepted,
        borderColor: Dropzone.Border.Active,
        border: '2px dashed',
      },
      text: {
        color: Dropzone.FontColor.DragAccepted,
      },
      _hover: {
        borderColor: Dropzone.Border.Active,
      },
      _active: {
        borderColor: Dropzone.Border.Active,
      },
    },
    [DropzoneVariant.FileAccepted]: {
      container: {
        cursor: 'default',
        justifyContent: 'space-between',
        borderColor: Dropzone.Border.FileAccepted,

        border: '1px solid',
        _hover: {
          borderColor: Dropzone.Border.FileAccepted,
          border: '1px solid',
        },
        _active: {
          borderColor: Dropzone.Border.FileAccepted,
          border: '1px solid',
        },
      },
      text: {
        color: Dropzone.FontColor.FileAccepted,
      },
      icon: {
        color: Dropzone.Icon,
      },
      clearButton: {
        color: Dropzone.FontColor.FileAccepted,
        textStyle: textStyleKeys.Body2,
      },
    },
  },
})

const themeKey = getName({ DropzoneStyle })
export const useDropzoneStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof dropzoneStyleParts>(themeKey, props)

export const createDropzoneStylesContext = () =>
  createCustomStyleContext<typeof dropzoneStyleParts>(themeKey)
